// Pure utility functions for visit-schedule-step transformations
import execute from 'app/components/pages/pad-menu/pad-initialization/components/steps/visit-schedule/schedulePrescriptionCalendars/execute';
import { generateCode } from './calendarVisitSchedule.schema';

/**
 * Creates a visit schedule item object.
 *
 * @param {Object} prescription - The prescription object containing at least an id.
 * @param {Object} dtEvent - The date/time event object with date and time properties.
 * @param {number} index - The index used in the unique ID generation.
 * @param {string} [key] - Optional unique key (generated if not provided).
 * @returns {Object} The constructed visit schedule item.
 */
export const createVisitScheduleItem = (padPrescription, dtEvent, index, key = generateCode()) => {
  const uniqueVisitKey = `${dtEvent.date}-${dtEvent.time}`;

  return {
    // Unique ID combining padPrescription id, date, time and index
    // id: `${padPrescription.id}-${dtEvent.date}-${dtEvent.time}-${index}`,
    key,
    uniqueVisitKey,
    date: dtEvent.date,
    time: dtEvent.time,
    padPrescriptionIds: [padPrescription.id]
  };
};

/**
 * Transforms an array of prescription schedules into a flattened load schedule array.
 * Each prescription is expanded into multiple execution events via the execute function.
 * The resulting array contains unique keys and UI flags for rendering.
 *
 * @param {Array} prescriptionSchedulesArray - Array of prescription schedule objects (filtered by padVisitId)
 * @param {Object} pad - PAD data needed for the execute function
 * @param {Array} existingSchedule - Existing schedule to preserve keys from
 * @returns {Array} Transformed schedule data with unique keys and UI flags
 *
 * @example
 * const transformed = transformPrescriptionScheduleToLoadSchedule(
 *   [{ id: 1, frequency: 'DAILY', ... }],
 *   { padId: 123, ... },
 *   existingSchedule
 * );
 * // Returns: [{ key: 'a123', date: '2025-01-15', time: '08:00', ... }, ...]
 */
export const transformPrescriptionScheduleToLoadSchedule = (prescriptionSchedulesArray, pad) => {
  if (!Array.isArray(prescriptionSchedulesArray) || prescriptionSchedulesArray.length === 0) {
    return [];
  }

  if (!pad) {
    return [];
  }

  const seenSchedules = new Set();

  let transformedSchedule = [];

  prescriptionSchedulesArray.forEach(padPrescription => {
    const padPrescriptionId = padPrescription.id;

    if (!padPrescription || !padPrescription.id) {
      return;
    }

    try {
      const dateTimeEvents = execute(padPrescription, pad);

      if (!Array.isArray(dateTimeEvents)) {
        return;
      }

      dateTimeEvents.forEach(dtEvent => {
        const uniqueVisitKey = `${dtEvent.date}-${dtEvent.time}`;

        if (!seenSchedules.has(uniqueVisitKey)) {
          seenSchedules.add(uniqueVisitKey);

          transformedSchedule.push(createVisitScheduleItem(padPrescription, dtEvent));
        } else {
          transformedSchedule = [...transformedSchedule].map(schedule => {
            if (schedule.uniqueVisitKey === uniqueVisitKey) {
              return {
                ...schedule,
                padPrescriptionIds: [...schedule.padPrescriptionIds, padPrescriptionId]
              };
            }

            return schedule;
          });
        }
      });
    } catch (error) {
      throw new Error('Something wrong!');
    }
  });

  return transformedSchedule;
};
